import {
  ANIMATION_LABEL,
  ANIMATION_ORDER,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { BlockAnimation } from '@matter_extractor/shared';
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Ban,
  Contrast,
  Maximize2,
  PanelLeftOpen,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * Les huit icônes d'animation, LES MÊMES PARTOUT (refonte modulaire §9.3;
 * planche `ModeAnimation`; issue #58).
 *
 * « Les huit icônes d'animation sont les mêmes partout, dans la pile, dans
 * l'onglet Animation du bloc et dans les pistes ; au survol leur libellé, au
 * clic l'aperçu rejoue l'animation. » Three surfaces, so one component and one
 * table - drawn twice, the pile and the tracks would have disagreed about
 * which arrow means « glissé depuis la gauche », and an operator would have had
 * to learn two alphabets for one page.
 *
 * The row is a RADIO GROUP for the reason `segmented.tsx` gives: eight named,
 * exclusive choices. `ANIMATION_ICON` is exported because the pile and the
 * tracks draw the CHOSEN one on its own, in grey, without offering the other
 * seven - the pile making it a gesture of its own since #139.
 *
 * A label is the drawn bubble of `components/ui/tooltip.tsx` AND the control's
 * accessible name. The two are the same string here, so `Tooltip` posts no
 * `aria-describedby`: a description identical to the name would read the same
 * word twice. The native `title` this file once carried is gone with issue
 * #103 - the design system draws the bubble in WHITE, so the reason
 * `version-history.tsx` used to give (a dark bubble in a light application)
 * rested on a premise that was never true.
 */

export const ANIMATION_ICON: Readonly<
  Record<BlockAnimation, ComponentType<{ className?: string; 'aria-hidden'?: boolean }>>
> = {
  none: Ban,
  fade: Contrast,
  wipe: PanelLeftOpen,
  zoom: Maximize2,
  'slide-left': ArrowRightToLine,
  'slide-right': ArrowLeftToLine,
  'slide-up': ArrowDownToLine,
  'slide-down': ArrowUpToLine,
};

export interface AnimationPickerProps {
  readonly value: BlockAnimation;
  readonly readOnly: boolean;
  /** « Au clic, l'aperçu rejoue l'animation » - the caller replays (§9.3). */
  readonly onChange: (next: BlockAnimation) => void;
  /** Names the group, because a page holds one per block in the tracks. */
  readonly label: string;
}

export function AnimationPicker({ value, readOnly, onChange, label }: AnimationPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-testid={PAGE_EDITOR_IDS.animationPick}
      /*
        Huit icônes sur UNE rangée, y compris dans la colonne de 300 px des
        pistes : repliées sur deux lignes, la légende de §9.3 - « chacune dessine
        son mouvement » - se lit comme deux groupes plutôt que comme une échelle.
      */
      className="flex flex-wrap gap-1"
    >
      {ANIMATION_ORDER.map((animation) => {
        const Icon = ANIMATION_ICON[animation];
        const chosen = animation === value;
        return (
          <Tooltip key={animation} label={ANIMATION_LABEL[animation]}>
            <button
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: eight icon buttons, not eight inputs - the icon IS the label.
              role="radio"
              aria-checked={chosen}
              aria-label={ANIMATION_LABEL[animation]}
              data-animation={animation}
              disabled={readOnly}
              onClick={() => onChange(animation)}
              className={cn(
                'inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                chosen
                  ? 'border-bleu-nuit bg-bleu-nuit text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
              )}
            >
              <Icon aria-hidden className="size-3.5" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
